import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip
} from '@material-ui/core';
import { Folder as FolderIcon, Undo as UndoIcon } from '@material-ui/icons';

import { FileDto } from '../Document.interface';
import DocumentFileExplorer from './DocumentFileExplorer';
import DocumentMultiUpload, {
  DocumentMultiUploadProps
} from './DocumentMultiUpload';

const StyledList = styled(List)`
  padding: 0;
  background-color: ${props => props.theme.palette.grey[100]};
  .MuiAvatar-root {
    background-color: ${props => props.theme.palette.primary.main};
  }
`;

export interface DocumentUploadDialogProps
  extends Pick<
    DocumentMultiUploadProps,
    'referenceData' | 'referenceTaskId' | 'referenceMetadata' | 'onDismiss'
  > {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * Opens a dialog to pick the Folder upload destination
 * before showing the dialog for uploading Files.
 */
const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = props => {
  const {
    open,
    referenceData,
    referenceTaskId,
    referenceMetadata,
    setOpen,
    onDismiss
  } = props;
  const [folder, setFolder] = useState<FileDto>();
  return (
    <>
      <FolderSelectDialog
        open={open}
        onCancel={() => setOpen(false)}
        onSelectFile={folder => {
          setFolder(folder);
          setOpen(false);
        }}
      />
      {folder && (
        <DocumentMultiUpload
          open
          folder={folder}
          referenceData={referenceData}
          referenceTaskId={referenceTaskId}
          referenceMetadata={referenceMetadata}
          onDismiss={onDismiss}
          setOpen={open => !open && setFolder(undefined)}
          renderContent={manager => (
            <Box mb={2} mx={3} boxShadow={1}>
              <FolderItem folder={folder}>
                <ListItemSecondaryAction>
                  <Tooltip arrow title='Select another folder'>
                    <IconButton
                      size='small'
                      disabled={manager.items.length > 0}
                      onClick={() => {
                        setFolder(undefined);
                        setOpen(true);
                      }}
                    >
                      <UndoIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </FolderItem>
            </Box>
          )}
        />
      )}
    </>
  );
};

export default DocumentUploadDialog;

interface FolderItemProps {
  folder: FileDto | undefined;
}

const FolderItem: React.FC<FolderItemProps> = props => {
  const { folder, children } = props;
  return (
    <StyledList dense>
      <ListItem>
        <ListItemAvatar>
          <Avatar>
            <FolderIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={folder?.name || '?'}
          secondary='Files will be uploaded to this selected folder.'
        />
        {children}
      </ListItem>
    </StyledList>
  );
};

interface FolderSelectDialogProps {
  open: boolean;
  onCancel?: () => void;
  onSelectFile?: (file: FileDto | undefined) => void;
}

const FolderSelectDialog: React.FC<FolderSelectDialogProps> = props => {
  const { open, onCancel, onSelectFile } = props;
  const [file, setFile] = useState<FileDto>();

  useEffect(() => {
    open && setFile(undefined);
  }, [open]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Select Destination Folder</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <DialogContentText>
            Select the destination folder for upload.
          </DialogContentText>
        </Box>
        <DocumentFileExplorer
          hideTrashDir
          selected={file ? file.id.toString() : undefined}
          onSelectFile={(_, file: FileDto) => {
            setFile(file?.isFolder ? file : undefined);
          }}
        />
        <Collapse in={!!file}>
          <Box mt={2} boxShadow={1}>
            <FolderItem folder={file} />
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!file}
          onClick={() => {
            // handle root dir
            onSelectFile?.(file?.id === -1 ? { ...file, id: 0 } : file);
          }}
        >
          Select Folder
        </Button>
      </DialogActions>
    </Dialog>
  );
};
